import React, {ReactEventHandler, useEffect, useRef, useState} from 'react';
import log from '@chancer/common/lib/utils/Log';
import {TMediaEntry} from '@chancer/common/lib/interfaces/firestore/FirestoreInterfaces';
import {
  getIsImage,
  getIsSocialEmbed,
  getIsVideo,
} from '@chancer/common/lib/utils/MediaUtils';

import './ScreenBackground.scss';
import {SocialEmbed} from './SocialEmbed';
import {IColorConfiguration} from '@chancer/common/lib/utils/ColorUtils';
import {COLOR_BACKGROUND} from '@chancer/components/lib/Styles/DesignSystem-chancer';

interface IProps {
  active: boolean;
  preload: boolean;
  mute: boolean;
  media: TMediaEntry | null;
  safeAreaTopPadding: number;
  colorConfig: IColorConfiguration;
  accentColor?: string;
  onMute: () => void;
}

enum Orientation {
  PORTRAIT = 'portrait',
  LANDSCAPE = 'landscape',
  SQUARE = 'square',
}

const animateVolume = (
  ref: HTMLVideoElement | null,
  start: number,
  end: number,
  duration: number,
) => {
  if (start === end) {
    return;
  }
  var range = end - start;
  var current = start;
  var stepTime = 1000 / 60; // 60 fps
  var increment = range / (duration / stepTime);
  var timer = window.setInterval(() => {
    current += increment;
    if (current >= end) {
      current = end;
    }
    if (ref) {
      ref.volume = current;
    } else {
      clearInterval(timer);
    }
    if (current === end) {
      clearInterval(timer);
    }
  }, stepTime);
};

export const ScreenBackground: React.FC<IProps> = (props) => {
  const {active, onMute} = props;
  const [retryCount, setRetryCount] = useState(0);
  const [orientation, setOrientation] = useState<Orientation>(
    Orientation.PORTRAIT,
  );
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.target as HTMLImageElement;
    const width = img.naturalWidth;
    const height = img.naturalHeight;

    if (width === height) {
      setOrientation(Orientation.SQUARE);
    } else if (width > height) {
      setOrientation(Orientation.LANDSCAPE);
    } else {
      setOrientation(Orientation.PORTRAIT);
    }
  };

  const handleVideoLoad = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.target as HTMLVideoElement;
    const width = video.videoWidth;
    const height = video.videoHeight;

    if (width === height) {
      setOrientation(Orientation.SQUARE);
    } else if (width > height) {
      setOrientation(Orientation.LANDSCAPE);
    } else {
      setOrientation(Orientation.PORTRAIT);
    }
  };

  useEffect(() => {
    async function tryPlay() {
      if (videoRef.current !== null) {
        if (active) {
          try {
            animateVolume(videoRef.current, 0, 1, 2000);
            await videoRef.current.play();
          } catch (err) {
            log.warning('Error playing video:', err);
            if (err instanceof Error && err.name === 'NotAllowedError') {
              if (videoRef.current.muted === false) {
                log.debug('Trying to play muted video');
                videoRef.current.muted = true;
                await videoRef.current.play();
                onMute();
              }
            }
          }
        } else {
          videoRef.current.pause();
        }
      }
    }
    tryPlay();
  }, [active, retryCount, onMute]);

  const _onVideoError: ReactEventHandler<HTMLVideoElement> = (event) => {
    log.warning('Video error event', event);
    if (retryCount < 2) {
      setRetryCount(retryCount + 1);
    }
  };

  if (props.media === null) {
    return <div className="screen_container__background" />;
  }

  if (getIsImage(props.media)) {
    videoRef.current = null;
    return (
      <div
        className="screen_container__background"
        style={
          props.active
            ? {
                ...(orientation === Orientation.PORTRAIT
                  ? {
                      animationName: 'kenburns1',
                      animationDuration: '45s',
                    }
                  : {}),
                paddingTop:
                  orientation === Orientation.SQUARE ||
                  orientation === Orientation.LANDSCAPE
                    ? props.safeAreaTopPadding + 56
                    : undefined,
              }
            : undefined
        }>
        {props.preload && (
          <>
            <div
              className="screen_container__background-gradient"
              style={
                props.accentColor
                  ? {backgroundImage: `linear-gradient(to bottom, transparent, ${props.accentColor})`}
                  : undefined
              }
            />
            <img
              alt={props.media.caption ? props.media.caption : ''}
              className="screen_container__background--image"
              src={props.media.image.url}
              onLoad={handleImageLoad}
            />
          </>
        )}
      </div>
    );
  } else if (getIsVideo(props.media)) {
    return (
      <div
        className="screen_container__background"
        style={
          props.active
            ? {
                ...(orientation === Orientation.PORTRAIT
                  ? {
                      animationName: 'kenburns1',
                      animationDuration: '45s',
                    }
                  : {}),
                paddingTop:
                  orientation === Orientation.SQUARE ||
                  orientation === Orientation.LANDSCAPE
                    ? props.safeAreaTopPadding + 56
                    : undefined,
              }
            : undefined
        }>
        {props.preload && (
          <>
            <div className="screen_container__background-gradient" />
            <video
              id="myVideo"
              key={`video${retryCount}`}
              ref={videoRef}
              className="screen_container__background--video"
              playsInline
              loop
              preload="auto"
              muted={props.mute}
              onLoadedMetadata={handleVideoLoad}
              onError={_onVideoError}>
              <source src={props.media.video.url} type="video/mp4" />
            </video>
          </>
        )}
      </div>
    );
  } else if (getIsSocialEmbed(props.media)) {
    // Portrait full-bleed embeds: TikTok and YouTube Shorts (9:16)
    const isPortrait =
      props.media.social.embed.includes('tiktok') ||
      (props.media.social.embed.includes('youtube') &&
        props.media.social.embed.includes('aspect-ratio: 9/16'));
    return (
      <div
        className="screen_container__embed-background"
        style={
          isPortrait
            ? {
                // Portrait embeds (TikTok, YouTube Shorts): full-bleed, no padding
                padding: 0,
                backgroundImage: `linear-gradient(${COLOR_BACKGROUND} 40%, ${props.colorConfig.questionBackgroundGradient})`,
              }
            : {
                paddingTop: props.safeAreaTopPadding + 56,
                backgroundImage: `linear-gradient(${COLOR_BACKGROUND} 40%, ${props.colorConfig.questionBackgroundGradient})`,
              }
        }>
        {props.active && (
          <>
            <div className="screen_container__background-gradient" />
            <SocialEmbed embed={props.media.social.embed} />
          </>
        )}
      </div>
    );
  } else {
    return null;
  }
};
